import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import { Suspense, useEffect, useMemo } from "react";
import * as THREE from "three";
import {
  paletteSlotFromMaterialName,
  resolvePaletteColors,
} from "../../shared/furniture-palette";
import {
  createFilledTiles,
  getTileHeight,
  TILE_WORLD_HEIGHT_UNIT,
} from "../../shared/room-layout";
import {
  normalizeCardinalRotationDegrees,
  resolveFurnitureFootprintCellsForAsset,
} from "../../shared/furniture-footprint";
import { createDefaultRoomThemeRuntime } from "../../shared/room-theme";
import { resolveSeatPoses } from "../../shared/seating";
import type {
  AvatarState,
  FurnitureColorVariant,
  FurnitureInteractionType,
  RoomAsset,
  RoomLayoutRuntime,
} from "../../shared/types";
import { normalizeGlb } from "../lib/glb-normalize";
import { DrobbitAvatar } from "./DrobbitAvatar";
import { IsometricRoom } from "./IsometricRoom";
import { NormalizedGlb } from "./NormalizedGlb";

/** Points the camera at the vertical center of the normalized model. */
function CameraTarget({ y, fitRadius }: { y: number; fitRadius: number }) {
  const { camera, size } = useThree();
  useEffect(() => {
    camera.lookAt(0, y, 0);
    if (camera instanceof THREE.OrthographicCamera) {
      const radius = Math.max(0.25, fitRadius);
      const fitZoom = (Math.min(size.width, size.height) * 0.84) / (radius * 2);
      camera.zoom = THREE.MathUtils.clamp(fitZoom, 18, 88);
    }
    camera.updateProjectionMatrix();
  }, [camera, y, fitRadius, size.width, size.height]);
  return null;
}

function PreviewModel({
  modelUrl,
  tintColor,
  rotation,
  paletteColors,
}: {
  modelUrl: string;
  tintColor?: string;
  rotation: number;
  paletteColors?: Record<string, string>;
}) {
  const gltf = useGLTF(modelUrl) as { scene: THREE.Group };

  const paletteColorMap = useMemo(() => {
    const map = new Map<string, THREE.Color>();
    if (!paletteColors || typeof paletteColors !== "object") {
      return map;
    }
    for (const [slot, colorValue] of Object.entries(paletteColors)) {
      if (typeof colorValue !== "string") {
        continue;
      }
      try {
        map.set(slot, new THREE.Color(colorValue));
      } catch {
        // Ignore invalid palette colors.
      }
    }
    return map;
  }, [paletteColors]);

  const { object, scale, offset, centerY, fitRadius } = useMemo(() => {
    const cloned = gltf.scene.clone(true);
    const tint = tintColor ? new THREE.Color(tintColor) : null;
    if (tint || paletteColorMap.size > 0) {
      cloned.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh) return;
        const mesh = child as THREE.Mesh;
        const src = mesh.material;
        const applyMaterialColor = (source: THREE.Material): THREE.Material => {
          const material = source.clone();
          if (
            !("color" in material) ||
            !(material.color instanceof THREE.Color)
          ) {
            return material;
          }
          const slot = paletteSlotFromMaterialName(material.name);
          const slotColor =
            slot != null ? paletteColorMap.get(slot) ?? null : null;
          if (slotColor) {
            material.color.copy(slotColor);
            return material;
          }
          if (tint) {
            material.color.lerp(tint, 0.72);
          }
          return material;
        };
        if (Array.isArray(src)) {
          mesh.material = src.map(applyMaterialColor);
        } else {
          mesh.material = applyMaterialColor(src);
        }
      });
    }
    const { scale: s, offset: o } = normalizeGlb(cloned);
    // Compute vertical center of the scaled model for camera framing
    const box = new THREE.Box3().setFromObject(cloned);
    const cy = ((box.min.y + box.max.y) / 2) * s + o.y;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const radius = Math.max(0.25, sphere.radius * s);
    return { object: cloned, scale: s, offset: o, centerY: cy, fitRadius: radius };
  }, [gltf.scene, paletteColorMap, tintColor]);

  return (
    <>
      <CameraTarget y={centerY} fitRadius={fitRadius} />
      <group position={[offset.x, offset.y, offset.z]}>
        <group rotation={[0, (rotation * Math.PI) / 180, 0]}>
          <primitive object={object} scale={[scale, scale, scale]} />
        </group>
      </group>
    </>
  );
}

function RoomPreviewCamera() {
  const { camera } = useThree();
  useEffect(() => {
    camera.lookAt(0, 0.8, 0);
    camera.updateProjectionMatrix();
  }, [camera]);
  return null;
}

const PREVIEW_LAYOUT: RoomLayoutRuntime = {
  id: "furniture-preview-layout",
  name: "Furniture Preview",
  access: "free",
  maxUsers: 1,
  width: 7,
  height: 7,
  tiles: createFilledTiles(7, 7),
  door: {
    x: -3,
    z: 0,
    direction: "west",
  },
};

const PREVIEW_THEME = createDefaultRoomThemeRuntime();

export function FurniturePreviewCanvas({
  modelUrl,
  tintColor,
  inRoom = false,
  interactive = false,
  rotation = 0,
  blockWidth = 1,
  blockDepth = 1,
  sittable = false,
  interactionType,
  walkableTop = false,
  stackableTop = false,
  stackHeight = 1,
  teleportGroup,
  actionOpenAnimation,
  actionCloseAnimation,
  seatOffsetX = 0,
  seatOffsetY = 0,
  seatOffsetZ = 0,
  colorVariants,
  defaultColorVariantId,
  colorVariantId,
  paletteColors,
}: {
  modelUrl: string;
  tintColor?: string;
  inRoom?: boolean;
  interactive?: boolean;
  rotation?: number;
  blockWidth?: number;
  blockDepth?: number;
  sittable?: boolean;
  interactionType?: FurnitureInteractionType;
  walkableTop?: boolean;
  stackableTop?: boolean;
  stackHeight?: number;
  teleportGroup?: string;
  actionOpenAnimation?: string;
  actionCloseAnimation?: string;
  seatOffsetX?: number;
  seatOffsetY?: number;
  seatOffsetZ?: number;
  colorSlots?: string[];
  colorVariants?: FurnitureColorVariant[];
  defaultColorVariantId?: string;
  colorVariantId?: string;
  paletteColors?: Record<string, string>;
}) {
  const resolvedInteractionType: FurnitureInteractionType =
    interactionType ?? (sittable ? "sit" : "none");
  const resolvedSittable =
    sittable ||
    resolvedInteractionType === "sit" ||
    resolvedInteractionType === "lay";
  const resolvedPaletteColors = useMemo(() => {
    if (paletteColors && Object.keys(paletteColors).length > 0) {
      return paletteColors;
    }
    return resolvePaletteColors(
      colorVariants,
      colorVariantId,
      defaultColorVariantId
    );
  }, [colorVariants, colorVariantId, defaultColorVariantId, paletteColors]);

  const previewAsset = useMemo<RoomAsset>(
    () => ({
      id: "preview-furniture",
      roomId: "preview-room",
      type: "glb",
      url: modelUrl,
      x: 0,
      y: 0,
      z: 0,
      size: 1,
      rotation: normalizeCardinalRotationDegrees(rotation),
      tintColor,
      blockWidth: Math.max(1, Math.round(blockWidth)),
      blockDepth: Math.max(1, Math.round(blockDepth)),
      sittable: resolvedSittable,
      interactionType: resolvedInteractionType,
      walkableTop,
      stackableTop,
      stackHeight,
      teleportGroup,
      actionOpenAnimation,
      actionCloseAnimation,
      seatOffsetX,
      seatOffsetY,
      seatOffsetZ,
      colorVariantId,
      paletteColors: resolvedPaletteColors,
    }),
    [
      modelUrl,
      rotation,
      tintColor,
      blockWidth,
      blockDepth,
      resolvedSittable,
      resolvedInteractionType,
      walkableTop,
      stackableTop,
      stackHeight,
      teleportGroup,
      actionOpenAnimation,
      actionCloseAnimation,
      seatOffsetX,
      seatOffsetY,
      seatOffsetZ,
      colorVariantId,
      resolvedPaletteColors,
    ]
  );

  const previewSeatPoses = useMemo(() => {
    if (!resolvedSittable) return null;
    const poses = resolveSeatPoses(previewAsset, (seatCellX, seatCellZ) => {
      return (
        getTileHeight(PREVIEW_LAYOUT, seatCellX, seatCellZ) *
        TILE_WORLD_HEIGHT_UNIT
      );
    });
    return poses.length > 0 ? poses : null;
  }, [previewAsset, resolvedSittable]);
  const previewAvatars = useMemo<AvatarState[]>(() => {
    if (!previewSeatPoses) {
      return [];
    }
    return previewSeatPoses.map((seatPose, index) => ({
      id: `preview-drobbit-${seatPose.seatIndex}`,
      address: `preview-${seatPose.seatIndex}`,
      name: "Drobbit",
      role: "player",
      isBot: false,
      color: index % 2 === 0 ? "#f08c2e" : "#f76707",
      isSitting: true,
      sittingAssetId: previewAsset.id,
      sittingSeatIndex: seatPose.seatIndex,
      x: seatPose.x,
      y: seatPose.y,
      z: seatPose.z,
      targetX: seatPose.x,
      targetY: seatPose.y,
      targetZ: seatPose.z,
      facing: seatPose.facing,
    }));
  }, [previewAsset.id, previewSeatPoses]);
  const previewFootprintCells = useMemo(() => {
    return resolveFurnitureFootprintCellsForAsset(previewAsset).map(
      ([x, z]) => ({ x, z })
    );
  }, [
    previewAsset.blockDepth,
    previewAsset.blockWidth,
    previewAsset.rotation,
    previewAsset.x,
    previewAsset.z,
  ]);

  if (inRoom) {
    return (
      <Canvas
        orthographic
        camera={{
          position: [8, 8, 8],
          zoom: 56,
          near: 0.1,
          far: 120,
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <RoomPreviewCamera />
        {interactive ? (
          <OrbitControls
            makeDefault
            enableRotate={false}
            enablePan
            enableZoom
            screenSpacePanning
            target={[0, 0.8, 0]}
            mouseButtons={{
              LEFT: THREE.MOUSE.ROTATE,
              MIDDLE: THREE.MOUSE.PAN,
              RIGHT: THREE.MOUSE.PAN,
            }}
            touches={{
              ONE: THREE.TOUCH.PAN,
              TWO: THREE.TOUCH.DOLLY_PAN,
            }}
          />
        ) : null}
        <ambientLight intensity={0.62} color="#fff8f0" />
        <hemisphereLight
          intensity={0.46}
          color="#c7dcff"
          groundColor="#f0d8bf"
        />
        <directionalLight
          position={[7, 12, 6]}
          intensity={0.95}
          color="#fff2dd"
        />
        <IsometricRoom layout={PREVIEW_LAYOUT} theme={PREVIEW_THEME} />
        {previewFootprintCells.map((cell) => {
          const floorY =
            getTileHeight(PREVIEW_LAYOUT, cell.x, cell.z) *
            TILE_WORLD_HEIGHT_UNIT;
          return (
            <mesh
              key={`preview-footprint-${cell.x}-${cell.z}`}
              rotation={[-Math.PI / 2, 0, 0]}
              position={[cell.x, floorY + 0.012, cell.z]}
            >
              <planeGeometry args={[0.9, 0.9]} />
              <meshBasicMaterial
                color="#fd7e14"
                transparent
                opacity={0.26}
                depthWrite={false}
                toneMapped={false}
              />
            </mesh>
          );
        })}
        <Suspense fallback={null}>
          <NormalizedGlb asset={previewAsset} layout={PREVIEW_LAYOUT} />
          {previewAvatars.map((avatar) => (
            <DrobbitAvatar
              key={avatar.id}
              avatar={avatar}
              isSelf={false}
              isLaying={resolvedInteractionType === "lay"}
              showChatBubbles={false}
            />
          ))}
        </Suspense>
      </Canvas>
    );
  }

  return (
    <Canvas
      orthographic
      camera={{
        position: [2, 1.5, 2],
        zoom: 55,
        near: 0.1,
        far: 50,
      }}
      style={{ width: "100%", height: "100%" }}
    >
      {interactive ? (
        <OrbitControls
          makeDefault
          enableRotate={false}
          enablePan
          enableZoom
          screenSpacePanning
          target={[0, 0.8, 0]}
          mouseButtons={{
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.PAN,
          }}
          touches={{
            ONE: THREE.TOUCH.PAN,
            TWO: THREE.TOUCH.DOLLY_PAN,
          }}
        />
      ) : null}
      <ambientLight intensity={0.7} color="#fff8f0" />
      <hemisphereLight intensity={0.4} color="#b8d8f0" groundColor="#e8d4b8" />
      <directionalLight position={[3, 5, 3]} intensity={1.0} color="#fff4e0" />
      <PreviewModel
        modelUrl={modelUrl}
        tintColor={tintColor}
        rotation={normalizeCardinalRotationDegrees(rotation)}
        paletteColors={resolvedPaletteColors}
      />
    </Canvas>
  );
}
